import { useMemo } from 'react';
import { useFim3Api } from '@/components/idea-management/hooks/useFim3Api';
import {
  headerToCustomFieldId,
  isHeaderCustomField,
} from '@/components/loop-visualizations/layout/header-menu/utils';
import type { ItemV2 } from '@/lib/api/types/items';
import type {
  IPropertySelectorByID,
  ISwimlaneHeader,
  ItemStatusOption,
  ItemTypeLevelOption,
  ItemTypeOption,
  IVisualizationHeader,
} from '@/lib/api/types/loop-visualization';
import type { Team } from '@/lib/api/types/teams';
import { ONE_HOUR_IN_MS } from '@/lib/constants';
import { useFlag } from '@/lib/hooks/feature-flags/useFlag';
import { useGetPlansQuery } from '@/lib/queries/adaptive-planner/useGetPlansQuery';
import {
  useGetCustomFieldDetailsQuery,
  usePicklistOptionsQuery,
} from '@/lib/queries/common/useGetCustomFieldsQuery';
import { useEstimationsQuery } from '@/lib/queries/common/useGetEstimationScheme';
import { useGetItemStatusesQuery } from '@/lib/queries/common/useGetItemStatusesQuery';
import { useItemTypeLevelsQuery } from '@/lib/queries/common/useItemTypeLevelsQuery';
import { useItemTypesQuery } from '@/lib/queries/common/useItemTypesQuery';
import { useGetTeamsQuery } from '@/lib/queries/teams/useGetTeamsQuery';
import { useTempoUsersQuery } from '@/lib/queries/user-management/tempoUserQueries';

const PICKLIST_TYPES = new Set(['PICKLIST', 'MULTI_PICKLIST']);
const USER_TYPES = new Set(['USER', 'MULTI_USER']);
const CUSTOMER_TYPES = new Set(['MULTI_CUSTOMER']);

function extractCustomFieldValue(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return String(value);
  if (typeof value === 'object' && !Array.isArray(value) && 'value' in value) {
    const inner = (value as { value: unknown }).value;
    return inner != null ? String(inner) : null;
  }
  return null;
}

function extractUniqueCustomFieldValues(items: ItemV2[], fieldId: string): string[] {
  const values = new Set<string>();
  for (const item of items) {
    const raw = item.customFieldValues?.[fieldId];
    if (raw == null) continue;
    if (Array.isArray(raw)) {
      for (const entry of raw) {
        const v = extractCustomFieldValue(entry);
        if (v != null) values.add(v);
      }
    } else {
      const v = extractCustomFieldValue(raw);
      if (v != null) values.add(v);
    }
  }
  return Array.from(values);
}

function extractUniqueCustomFieldNumberValues(items: ItemV2[], fieldId: string): number[] {
  return extractUniqueCustomFieldValues(items, fieldId)
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value > 0)
    .sort((a, b) => a - b);
}

// Build the ParentItemSelector result from the available item types/levels
const buildParentItemSelector = (
  items: ItemV2[],
  allItemsById: ReadonlyMap<number, Partial<ItemV2>> | undefined,
  itemLevelId: number,
  itemTypeLevels?: ItemTypeLevelOption[],
  itemTypes?: ItemTypeOption[],
): IVisualizationHeader | null => {
  if (!itemTypeLevels || !itemTypes) return null;

  // Find the numeric level (1..n) for the current item-level id
  const currentLevel = itemTypeLevels.find((l) => l.id === itemLevelId)?.level;
  if (currentLevel == null) return null;

  const orderedLevels = Array.from(new Set(itemTypeLevels.map((l) => l.level))).sort(
    (a, b) => a - b,
  );

  // Determine the previous (parent) numeric level; default to 0 when none exist
  const currentIndex = orderedLevels.indexOf(currentLevel);
  if (currentIndex === 0) {
    return null;
  }
  const parentLevelNumber = orderedLevels[currentIndex - 1];

  // Retrieve id of all parent itemTypeLevels
  const parentLevelIds = new Set(
    itemTypeLevels.filter((l) => l.level === parentLevelNumber).map((l) => l.id),
  );

  // Get ids of items with an itemTypeId in the list of parent itemTypeIds
  const parentItemTypeIds = new Set(
    itemTypes.filter((it) => parentLevelIds.has(it.itemLevelId)).map((it) => it.id),
  );
  const alreadySeen = new Set<number>();

  // allItemsById contains all hierarchy levels; items contains only target-level items.
  // We need parent-level items, which exist in allItemsById but not in items.
  const sourceItems = allItemsById ? Array.from(allItemsById.values()) : items;
  const options = sourceItems.filter((itm): itm is ItemV2 => {
    if (
      itm.id != null &&
      itm.itemTypeId != null &&
      parentItemTypeIds.has(itm.itemTypeId) &&
      !alreadySeen.has(itm.id)
    ) {
      alreadySeen.add(itm.id);
      return true;
    }
    return false;
  });

  return { type: 'ParentItemSelector', options };
};

/**
 * Fetch the data based on the header type in order to generate the headers.
 * Ensure queries don't run when the header is not of the expected type
 */
export const useVisualizationHeaderOptions = (
  header: IPropertySelectorByID | null,
  items: ItemV2[],
  itemLevelId: number,
  {
    itemTypes: itemTypesOverride,
    itemLevels: itemLevelsOverride,
    itemStatuses: itemStatusesOverride,
    teamsOverride,
    enabled = true,
    allItemsById,
  }: {
    itemTypes?: ItemTypeOption[];
    itemLevels?: ItemTypeLevelOption[];
    itemStatuses?: ItemStatusOption[];
    teamsOverride?: Team[];
    enabled?: boolean;
    allItemsById?: ReadonlyMap<number, Partial<ItemV2>>;
  } = {},
): ISwimlaneHeader | IVisualizationHeader | null => {
  const isCreatedByHeader = header?.type === 'createdBy';
  const customFieldId = headerToCustomFieldId(header);
  const isCustomField =
    header?.type === 'CustomPropertySelectorByID' && isHeaderCustomField(header.customPropertyID);
  const { customersApi } = useFim3Api();
  const ideaManagerEnabled = useFlag('ideaManagerEnabled');

  const { data: customFieldDetails } = useGetCustomFieldDetailsQuery({
    id: enabled && header ? customFieldId : 0,
  });
  const customFieldType = isCustomField ? customFieldDetails?.type : undefined;
  const isPicklistField = customFieldType != null && PICKLIST_TYPES.has(customFieldType);
  const isUserField = customFieldType != null && USER_TYPES.has(customFieldType);
  const isMultiCustomerField = customFieldType != null && CUSTOMER_TYPES.has(customFieldType);
  const isCustomerField = ideaManagerEnabled && isMultiCustomerField;
  const { data: picklistOptions } = usePicklistOptionsQuery({
    customFieldId: isPicklistField ? customFieldId : 0,
  });
  const { data: fetchedItemStatuses } = useGetItemStatusesQuery({
    enabled:
      enabled &&
      itemStatusesOverride == null &&
      header?.type === 'CustomPropertySelectorByID' &&
      header.customPropertyID === 'status',
  });
  const { data: fetchedItemTypes } = useItemTypesQuery({
    enabled: enabled && header?.type === 'ParentItemSelector' && itemTypesOverride == null,
    staleTime: ONE_HOUR_IN_MS,
  });
  const { data: fetchedItemLevels } = useItemTypeLevelsQuery({
    enabled: enabled && header?.type === 'ParentItemSelector' && itemLevelsOverride == null,
    staleTime: ONE_HOUR_IN_MS,
  });
  const { data: estimations } = useEstimationsQuery({
    enabled:
      enabled &&
      header?.type === 'CustomPropertySelectorByID' &&
      header.customPropertyID === 'estimationId',
  });
  const { data: teams } = useGetTeamsQuery({
    enabled:
      enabled &&
      header?.type === 'CustomPropertySelectorByID' &&
      header.customPropertyID === 'team',
  });
  const { data: plans } = useGetPlansQuery({
    enabled:
      enabled &&
      header?.type === 'CustomPropertySelectorByID' &&
      header.customPropertyID === 'adaptivePlannerPlan',
  });

  const uniqueCreatorIds = useMemo(() => {
    if (!isCreatedByHeader) return [];
    return Array.from(new Set(items.map((item) => item.createdBy))).filter(Boolean);
  }, [isCreatedByHeader, items]);

  const uniqueUserFieldIds = useMemo(() => {
    if (!isCustomField || !isUserField) return [];
    return extractUniqueCustomFieldValues(items, String(customFieldId));
  }, [isCustomField, isUserField, items, customFieldId]);

  const uniqueCustomerFieldIds = useMemo(() => {
    if (!isCustomField || !isCustomerField) return [];
    return extractUniqueCustomFieldNumberValues(items, String(customFieldId));
  }, [isCustomField, isCustomerField, items, customFieldId]);

  const { data: createdByUsers } = useTempoUsersQuery({
    filterBy:
      isCreatedByHeader && uniqueCreatorIds.length > 0
        ? { field: 'id', operator: 'in', value: uniqueCreatorIds }
        : undefined,
    sortBy: 'name',
    sortOrder: 'asc',
    enabled: enabled && isCreatedByHeader && uniqueCreatorIds.length > 0,
  });

  const { data: customFieldUsers } = useTempoUsersQuery({
    filterBy:
      isUserField && uniqueUserFieldIds.length > 0
        ? { field: 'id', operator: 'in', value: uniqueUserFieldIds }
        : undefined,
    enabled: isUserField && uniqueUserFieldIds.length > 0,
  });

  const { data: customerHydration } = customersApi.useGetSome(
    ideaManagerEnabled ? uniqueCustomerFieldIds : [],
  );

  const uniqueScalarValues = useMemo(() => {
    if (!isCustomField || isPicklistField || isUserField || isMultiCustomerField) return [];
    return extractUniqueCustomFieldValues(items, String(customFieldId));
  }, [isCustomField, isPicklistField, isUserField, isMultiCustomerField, items, customFieldId]);

  const itemTypes: ItemTypeOption[] | undefined = itemTypesOverride ?? fetchedItemTypes;
  const itemLevels: ItemTypeLevelOption[] | undefined = itemLevelsOverride ?? fetchedItemLevels;
  const itemStatuses: ItemStatusOption[] | undefined = itemStatusesOverride ?? fetchedItemStatuses;

  return useMemo(() => {
    if (!header) {
      return null;
    }

    switch (header.type) {
      case 'ParentItemSelector': {
        return buildParentItemSelector(items, allItemsById, itemLevelId, itemLevels, itemTypes);
      }
      case 'CustomPropertySelectorByID': {
        if (isHeaderCustomField(header.customPropertyID)) {
          if (isMultiCustomerField && !ideaManagerEnabled) {
            return null;
          }
          if (isPicklistField) {
            return { ...header, customFieldType, options: picklistOptions ?? [] };
          }
          if (isUserField) {
            const userOptions = (customFieldUsers ?? []).map((u) => ({
              id: u.id,
              name: u.name ?? u.email,
            }));
            return { ...header, customFieldType, options: userOptions };
          }
          if (isCustomerField) {
            const customerOptions = (customerHydration?.data ?? [])
              .map((customer) => ({
                id: customer.id,
                name: customer.name,
                logoUrl: customer.logoUrl,
              }))
              .sort((a, b) => a.name.localeCompare(b.name));
            return { ...header, customFieldType, options: customerOptions };
          }
          return {
            ...header,
            customFieldType,
            options: uniqueScalarValues.map((v) => ({ id: v, name: v })),
          };
        } else if (header.customPropertyID === 'status') {
          return {
            ...header,
            customPropertyID: 'status',
            options: itemStatuses ?? [],
          };
        } else if (header.customPropertyID === 'team') {
          return {
            ...header,
            customPropertyID: 'team',
            options: teamsOverride ?? teams?.data ?? [],
          };
        } else if (header.customPropertyID === 'adaptivePlannerPlan') {
          return {
            ...header,
            customPropertyID: 'adaptivePlannerPlan',
            options: plans ?? [],
          };
        } else if (header.customPropertyID === 'estimationId') {
          return {
            ...header,
            customPropertyID: 'estimationId',
            options: (estimations ?? []).map((e) => ({ id: e.id, name: e.label })),
          };
        }
        return null;
      }
      case 'DateSelector':
        return { ...header };
      case 'createdBy':
        return { type: 'createdBy', options: createdByUsers ?? [] };
      default:
        return null;
    }
  }, [
    header,
    items,
    itemLevelId,
    itemLevels,
    itemTypes,
    picklistOptions,
    itemStatuses,
    teamsOverride,
    teams?.data,
    plans,
    estimations,
    createdByUsers,
    isPicklistField,
    isUserField,
    isMultiCustomerField,
    isCustomerField,
    customFieldType,
    customFieldUsers,
    customerHydration,
    ideaManagerEnabled,
    uniqueScalarValues,
    allItemsById,
  ]);
};
